import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LEGAL_DOCUMENTS } from "@/lib/legalDocuments";

const STORAGE_KEY = "hh_cookie_prefs_v1";
const CONSENT_VERSION = "cookie-consent-v2.0-2026-07";

const DEFAULT_PREFS = {
  strictly_necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.version === CONSENT_VERSION) return parsed;
    return null;
  } catch {
    return null;
  }
}

function savePrefs(prefs) {
  const record = {
    version: CONSENT_VERSION,
    prefs,
    ts: new Date().toISOString(),
    pref_id: crypto.randomUUID?.() || `p_${Date.now()}`,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // storage disabled — recording best-effort
  }
  // Fire-and-forget: log to backend for audit history
  api.post("/cookie-preferences", record).catch(() => {});
  // Notify listeners so analytics/marketing modules can gate their loading
  window.dispatchEvent(new CustomEvent("hh:cookie-preferences", { detail: prefs }));
}

const CATEGORIES = [
  {
    key: "strictly_necessary",
    label: "Strictly Necessary",
    always: true,
    desc: "Required for the site to function — session security, form submission, fraud prevention.",
  },
  {
    key: "functional",
    label: "Functional",
    desc: "Remember choices like form progress, accessibility settings, and previously entered information.",
  },
  {
    key: "analytics",
    label: "Analytics",
    desc: "Help us understand how visitors interact with the site so we can improve it.",
  },
  {
    key: "marketing",
    label: "Marketing",
    desc: "Measure advertising effectiveness and personalize promotional content, where enabled.",
  },
];

function PrefsModal({ initial, onSave, onClose }) {
  const [prefs, setPrefs] = useState(initial);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-prefs-title"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="cookie-preferences-modal"
    >
      <div className="w-full max-w-lg rounded-3xl bg-white border border-[#E8DFD8] shadow-[0_30px_80px_rgba(42,28,20,0.25)] p-8">
        <h2 id="cookie-prefs-title" className="font-serif-lux text-2xl text-[#2A1C14]">
          Cookie Preferences
        </h2>
        <p className="text-sm text-[#5A3B31] mt-2">
          Choose which cookie categories to allow. Strictly necessary cookies are always active. See our{" "}
          <Link
            to="/cookies"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[#2A1C14]"
          >
            Cookie Policy
          </Link>
          .
        </p>

        <div className="mt-6 space-y-3">
          {CATEGORIES.map((c) => (
            <label
              key={c.key}
              className={`flex items-start gap-3 p-4 rounded-2xl border ${c.always ? "border-[#E8DFD8] bg-[#F4EEE8]" : "border-[#E8DFD8]"}`}
            >
              <input
                type="checkbox"
                data-testid={`cookie-pref-${c.key}`}
                checked={!!prefs[c.key]}
                disabled={!!c.always}
                onChange={(e) => setPrefs({ ...prefs, [c.key]: e.target.checked })}
                className="mt-1 accent-[#2A1C14]"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#2A1C14]">
                  {c.label}
                  {c.always ? (
                    <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-[#8C7A70]">
                      Always active
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-[#5A3B31] mt-1 leading-relaxed">{c.desc}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            data-testid="cookie-prefs-cancel"
            className="text-sm text-[#5A3B31] hover:text-[#2A1C14] py-2.5 px-5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(prefs)}
            data-testid="cookie-prefs-save"
            className="btn-primary text-sm justify-center"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CookieBanner() {
  const [prefs, setPrefs] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);

  useEffect(() => {
    const existing = loadPrefs();
    if (existing) {
      setPrefs(existing.prefs);
    } else {
      setShowBanner(true);
    }
  }, []);

  // Global event so any page (e.g. Cookie Policy) can open preferences.
  useEffect(() => {
    const handler = () => setShowPrefs(true);
    window.addEventListener("hh:open-cookie-preferences", handler);
    return () => window.removeEventListener("hh:open-cookie-preferences", handler);
  }, []);

  const commit = useCallback((next) => {
    savePrefs(next);
    setPrefs(next);
    setShowBanner(false);
    setShowPrefs(false);
  }, []);

  const acceptAll = () => commit({ strictly_necessary: true, functional: true, analytics: true, marketing: true });
  const rejectNonEssential = () => commit({ ...DEFAULT_PREFS });

  return (
    <>
      {showBanner ? (
        <div
          className="fixed bottom-4 inset-x-4 sm:bottom-6 sm:right-6 sm:left-auto z-40 max-w-lg print:hidden"
          role="region"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div className="rounded-2xl bg-[#2A1C14] text-[#FCFAF8] shadow-[0_20px_60px_rgba(42,28,20,0.35)] p-6 border border-[#5A3B31]">
            <p className="font-serif-lux text-xl leading-tight">
              We use cookies to improve your experience.
            </p>
            <p className="text-xs text-[#CBA4A4] mt-3 leading-relaxed">
              Strictly necessary cookies keep the site secure. Others help us measure how the site is used, personalize content, and support future services. See our{" "}
              <Link
                to="/cookies"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-[#F3D8DA]"
                data-testid="cookie-banner-policy-link"
              >
                Cookie Policy
              </Link>{" "}
              and{" "}
              <Link
                to={LEGAL_DOCUMENTS.privacy.path}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-[#F3D8DA]"
                data-testid="cookie-banner-privacy-link"
              >
                Privacy Policy
              </Link>
              .
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={rejectNonEssential}
                data-testid="cookie-banner-reject"
                className="flex-1 text-sm py-2.5 px-4 rounded-full border border-[#CBA4A4] text-[#F3D8DA] hover:bg-[#5A3B31] transition"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                data-testid="cookie-banner-manage"
                className="flex-1 text-sm py-2.5 px-4 rounded-full border border-[#CBA4A4] text-[#F3D8DA] hover:bg-[#5A3B31] transition"
              >
                Manage preferences
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-banner-accept"
                className="flex-1 text-sm py-2.5 px-4 rounded-full bg-[#F3D8DA] text-[#2A1C14] hover:bg-[#FCFAF8] transition"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showPrefs ? (
        <PrefsModal
          initial={prefs || DEFAULT_PREFS}
          onSave={commit}
          onClose={() => setShowPrefs(false)}
        />
      ) : null}
    </>
  );
}
